import { Content } from '@prismicio/client';
import { SliceComponentProps } from '@prismicio/react';
import { Text } from 'components/@tedui';
import ContentContainer from 'components/content-container';
import Image from 'components/image';
import { Link } from 'components/router';
import { motion } from 'motion/react';

// Animation variants
const containerVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.6,
      staggerChildren: 0.1
    }
  }
};

const headerVariants = {
  hidden: { opacity: 0, y: 30 },
  visible: {
    opacity: 1,
    y: 0,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.4, 0.25, 1]
    }
  }
};

const podcastsGridVariants = {
  hidden: { opacity: 0 },
  visible: {
    opacity: 1,
    transition: {
      duration: 0.4,
      staggerChildren: 0.15,
      delayChildren: 0.2
    }
  }
};

const podcastCardVariants = {
  hidden: { opacity: 0, y: 40, scale: 0.95 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: {
      duration: 0.8,
      ease: [0.25, 0.4, 0.25, 1]
    }
  }
};

/**
 * Props for `Podcasts`.
 */
export type PodcastsProps = SliceComponentProps<Content.PodcastsSlice>;

/**
 * Component for "Podcasts" Slices.
 */
const Podcasts = ({ slice }: PodcastsProps): JSX.Element => {
  const primary = slice.primary;

  return (
    <motion.section
      data-slice-type={slice.slice_type}
      data-slice-variation={slice.variation}
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <ContentContainer className="bg-white pb-16 lg-tui:pb-20">
        <motion.div
          className="mb-4 h-px w-full bg-black md-tui:mb-6"
          variants={headerVariants}
        />

        <div className="flex flex-col justify-between gap-6 md-tui:gap-10 lg-tui:gap-6">
          {/* Section Title */}
          {primary.title && (
            <motion.div variants={headerVariants}>
              <Text
                variant="header2"
                UNSAFE_className="flex-1 xs-tui:text-tui-5xl xs-tui:leading-[110%] xs-tui:-tracking-[1.2px] md-tui:text-tui-7xl md-tui:leading-[105%] md-tui:-tracking-[1.8px]"
                useNewTextStyles
              >
                {primary.title}
              </Text>
            </motion.div>
          )}

          {/* Podcasts Grid */}
          {slice.items && slice.items.length > 0 && (
            <motion.div
              className="flex flex-col gap-6 md-tui:flex-row md-tui:items-stretch md-tui:gap-10"
              variants={podcastsGridVariants}
            >
              {slice.items.map((item, index) => (
                <motion.div
                  key={index}
                  className="flex flex-1"
                  variants={podcastCardVariants}
                  initial="rest"
                  whileHover="hover"
                >
                  <motion.div className="flex h-full flex-col md-tui:gap-2">
                    {'url' in item.podcast_link && item.podcast_link.url ? (
                      <Link
                        href={item.podcast_link.url}
                        className="flex flex-1 gap-4 md-tui:gap-10"
                      >
                        {/* Podcast Image */}
                        {item.podcast_image?.url && (
                          <div className="shrink-0">
                            <motion.div className="relative aspect-square overflow-hidden rounded-sm">
                              <Image
                                src={item.podcast_image.url}
                                width={175}
                                height={175}
                                className="h-full max-w-[124px] object-cover transition-transform duration-700 ease-out lg-tui:max-w-full"
                                alt={item.podcast_title || 'Podcast image'}
                                sizes="(max-width: 768px) 124px, 175px"
                              />
                            </motion.div>
                          </div>
                        )}

                        <div className="flex flex-1 flex-col justify-between gap-3">
                          <div className="flex flex-col gap-3">
                            <div className="flex flex-col gap-2">
                              {/* Podcast Title */}
                              {item.podcast_title && (
                                <Text variant="header4" useNewTextStyles>
                                  {item.podcast_title}
                                </Text>
                              )}

                              {/* Podcast Description */}
                              {item.podcast_description && (
                                <Text variant="body2" useNewTextStyles>
                                  {item.podcast_description}
                                </Text>
                              )}
                            </div>
                          </div>
                        </div>
                      </Link>
                    ) : (
                      <div className="flex flex-1 gap-4 md-tui:gap-10">
                        {/* Content without link when URL is not available */}
                        {item.podcast_image?.url && (
                          <div className="shrink-0">
                            <motion.div className="relative aspect-square h-full overflow-hidden rounded-sm">
                              <Image
                                src={item.podcast_image.url}
                                width={175}
                                height={175}
                                className="h-full max-w-[124px] object-cover transition-transform duration-700 ease-out lg-tui:max-w-full"
                                alt={item.podcast_title || 'Podcast image'}
                                sizes="(max-width: 768px) 124px, 175px"
                              />
                            </motion.div>
                          </div>
                        )}

                        <div className="flex flex-1 flex-col justify-between gap-3">
                          <div className="flex flex-col gap-3">
                            <div className="flex flex-col gap-2">
                              {/* Podcast Title */}
                              {item.podcast_title && (
                                <Text variant="header4" useNewTextStyles>
                                  {item.podcast_title}
                                </Text>
                              )}

                              {/* Podcast Description */}
                              {item.podcast_description && (
                                <Text variant="body2" useNewTextStyles>
                                  {item.podcast_description}
                                </Text>
                              )}
                            </div>
                          </div>
                        </div>
                      </div>
                    )}
                  </motion.div>
                </motion.div>
              ))}
            </motion.div>
          )}
        </div>
      </ContentContainer>
    </motion.section>
  );
};

export default Podcasts;
